import type { FlowDefinition, FlowNode } from '../schema/flow'
import type { Block, InputBlock, PageDesign } from '../schema/blocks'
import { isInputBlock } from '../schema/blocks'
import type { Option } from '../schema/common'
import { optionValue } from '../schema/common'
import type { AnswerValue, StepAnswers } from '../schema/answers'
import { isAnswered, asStringArray, asPhotoRefs } from '../schema/answers'
import { visibleBlocks } from './visibility'

/**
 * ★ กฎกลางของระบบ: ไม่เก็บข้อมูลที่คำนวณได้
 *
 * รายการฟิลด์ของโหนดคำนวณจาก page.blocks (ถ้ามีหน้าที่ออกแบบเอง) ไม่งั้นจากชนิดโหนด
 * ทุกส่วน — chip picker ของเงื่อนไข, การตรวจคำตอบ, เว็บแอปจริง, หน้าหลังบ้าน —
 * เรียกฟังก์ชันเดียวกันนี้ จึงไม่มีทาง "ไม่ตรงกัน" แบบระบบเดิมที่ sync ฟิลด์สองทาง
 */

export type FieldKind = 'text' | 'number' | 'single' | 'multi' | 'photo' | 'gps'

export type FieldInfo = {
  name: string
  label: string
  kind: FieldKind
  required: boolean
  /** ห้ามค่าซ้ำกับคำตอบของงานอื่นในแบบสอบถามเดียวกัน — ตรวจจริงฝั่งเซิร์ฟเวอร์เท่านั้น */
  unique: boolean
  /** เช็คกับชุดข้อมูลอ้างอิงเก่าที่แอดมินนำเข้าไว้ — ตรวจจริงฝั่งเซิร์ฟเวอร์เท่านั้น */
  checkAgainstLookup: boolean
  /** ข้อความใน popup ตอนเจอว่าตรงกับข้อมูลเก่า — เว้นว่าง = ใช้ข้อความมาตรฐาน */
  lookupMatchMessage: string
  /** ชื่อกลุ่มสรุปในไฟล์ส่งออก — เว้นว่าง = ไม่รวมกลุ่ม */
  summaryGroup: string
  options: Option[]
  maxSelect: number | null
  maxLength: number | null
  /** มาจากหน้าที่ออกแบบเองหรือจากชนิดของโหนด */
  origin: 'page' | 'node'
}

export const DEFAULT_ANSWER_FIELD = 'answer'
export const DEFAULT_PHOTO_FIELD = 'photos'

function fieldFromBlock(block: InputBlock): FieldInfo {
  const base = {
    name: block.fieldName,
    label: block.label || block.fieldName,
    required: block.required,
    unique: block.unique,
    checkAgainstLookup: block.checkAgainstLookup,
    summaryGroup: block.summaryGroup,
    lookupMatchMessage: block.lookupMatchMessage,
    options: [] as Option[],
    maxSelect: null as number | null,
    maxLength: null as number | null,
    origin: 'page' as const,
  }
  switch (block.type) {
    case 'textInput':
      return {
        ...base,
        kind: block.inputMode === 'number' ? 'number' : 'text',
        maxLength: block.maxLength,
      }
    case 'textarea':
      return { ...base, kind: 'text', maxLength: block.maxLength }
    case 'select':
    case 'radioGroup':
      return { ...base, kind: 'single', options: block.options }
    case 'checkboxGroup':
      return { ...base, kind: 'multi', options: block.options, maxSelect: block.maxSelect }
    case 'cameraCapture':
      return { ...base, kind: 'photo', maxSelect: block.maxPhotos }
    case 'gpsCapture':
      return { ...base, kind: 'gps' }
    // ตัวเลือกของ 3 ชนิดนี้คำนวณจากข้อมูลจังหวัด/อำเภอ/ตำบลตอนแสดงผลจริง (โหลดแบบ lazy)
    // ไม่ใช่ค่าคงที่ที่ประกาศไว้ล่วงหน้า จึงถือเป็นฟิลด์ชนิดข้อความธรรมดาในระดับ engine
    case 'provinceSelect':
    case 'districtSelect':
    case 'subdistrictSelect':
      return { ...base, kind: 'text', maxLength: 120 }
  }
}

function fieldsFromNodeType(node: FlowNode): FieldInfo[] {
  const base = {
    name: DEFAULT_ANSWER_FIELD,
    label: node.title || 'คำตอบ',
    required: node.required,
    unique: false,
    checkAgainstLookup: false,
    lookupMatchMessage: '',
    summaryGroup: '',
    options: [] as Option[],
    maxSelect: null as number | null,
    maxLength: null as number | null,
    origin: 'node' as const,
  }
  switch (node.type) {
    case 'inputText':
      return [
        {
          ...base,
          kind: node.inputMode === 'number' ? 'number' : 'text',
          maxLength: node.multiline ? 1000 : 200,
        },
      ]
    case 'dropdown':
    case 'buttonChoice':
      return [{ ...base, kind: 'single', options: node.options }]
    case 'multiSelect':
      return [{ ...base, kind: 'multi', options: node.options, maxSelect: node.maxSelect }]
    case 'camera':
      return [
        {
          ...base,
          name: DEFAULT_PHOTO_FIELD,
          label: node.title || 'รูปภาพ',
          kind: 'photo',
          maxSelect: node.maxPhotos,
        },
      ]
    case 'form':
    case 'decision':
    case 'end':
      return []
  }
}

/** รายการฟิลด์ทั้งหมดของโหนด — แหล่งความจริงเดียวของทั้งระบบ */
export function getNodeFields(node: FlowNode): FieldInfo[] {
  if (node.page && node.page.blocks.length > 0) {
    return node.page.blocks.filter(isInputBlock).map(fieldFromBlock)
  }
  return fieldsFromNodeType(node)
}

export function getNodeField(node: FlowNode, fieldName: string): FieldInfo | undefined {
  return getNodeFields(node).find((field) => field.name === fieldName)
}

/** ฟิลด์แรกของโหนด — ใช้เมื่อเงื่อนไขอ้างถึงโหนดโดยไม่ระบุชื่อฟิลด์ (เช่น `Q3 = "ใช่"`) */
export function getPrimaryField(node: FlowNode): FieldInfo | undefined {
  return getNodeFields(node)[0]
}

export function getNodeOptions(node: FlowNode, fieldName: string): Option[] {
  return getNodeField(node, fieldName)?.options ?? []
}

/** หาโหนดจากรหัสอ้างอิง (Q1, Q2, ...) */
export function findNodeByKey(def: FlowDefinition, nodeKey: string): FlowNode | undefined {
  return def.nodes.find((node) => node.key === nodeKey)
}

export function findNodeById(def: FlowDefinition, nodeId: string): FlowNode | undefined {
  return def.nodes.find((node) => node.id === nodeId)
}

/**
 * ทำให้ชื่อฟิลด์ไม่ซ้ำกัน (field, field_2, field_3, ...)
 * เรียกทุกครั้งที่เพิ่ม/คัดลอก/เปลี่ยนชื่อบล็อกรับคำตอบ — ต้นเหตุของบั๊ก
 * "ลาก Radio มาสองอันแล้วเลือกไม่ได้" ในระบบเดิมคือชื่อ/ค่าซ้ำกัน
 */
export function uniquifyFieldName(existing: readonly string[], base: string): string {
  const taken = new Set(existing)
  const cleaned = base.trim() || 'field'
  if (!taken.has(cleaned)) return cleaned
  for (let n = 2; n < 1000; n += 1) {
    const candidate = `${cleaned}_${n}`
    if (!taken.has(candidate)) return candidate
  }
  return `${cleaned}_${Date.now()}`
}

/** ทำให้ค่าของตัวเลือกไม่ซ้ำกันภายในบล็อกเดียว */
export function uniquifyOptionValue(existing: readonly string[], base: string): string {
  return uniquifyFieldName(existing, base)
}

/**
 * กำหนดรหัส Q ให้โหนดที่ยังไม่มี โดยไม่แตะรหัสเดิม
 * (รหัสเดิมถูกอ้างในเงื่อนไข การเปลี่ยนรหัสจะทำให้เส้นทางพัง)
 */
export function deriveNodeKeys(nodes: FlowNode[]): FlowNode[] {
  // รอบแรก: จองรหัสที่ถูกต้องและยังไม่ซ้ำเอาไว้ก่อน โหนดเหล่านี้จะไม่ถูกแตะ
  const used = new Set<string>()
  const keepsOwnKey = new Set<string>()
  for (const node of nodes) {
    if (/^Q\d+$/.test(node.key) && !used.has(node.key)) {
      used.add(node.key)
      keepsOwnKey.add(node.id)
    }
  }
  // รอบสอง: แจกรหัสว่างให้โหนดที่เหลือ (ไม่มีรหัส/รหัสผิดรูปแบบ/รหัสซ้ำ)
  let next = 1
  return nodes.map((node) => {
    if (keepsOwnKey.has(node.id)) return node
    while (used.has(`Q${next}`)) next += 1
    const key = `Q${next}`
    used.add(key)
    return { ...node, key }
  })
}

/** สร้างบล็อกมาตรฐานของโหนด — ใช้ทั้งเป็น fallback ตอนรัน และเป็นจุดตั้งต้นตอนเริ่มออกแบบหน้า */
export function buildFallbackBlocks(node: FlowNode): Block[] {
  const blocks: Block[] = []
  if (node.title) {
    blocks.push({ id: `${node.id}__title`, visibleWhen: null, type: 'heading', text: node.title, level: 2 })
  }
  if (node.description) {
    blocks.push({ id: `${node.id}__desc`, visibleWhen: null, type: 'paragraph', text: node.description })
  }

  const inputId = `${node.id}__input`
  switch (node.type) {
    case 'inputText':
      blocks.push(
        node.multiline
          ? {
              id: inputId,
              visibleWhen: null,
              type: 'textarea',
              fieldName: DEFAULT_ANSWER_FIELD,
              label: '',
              required: node.required,
              unique: false,
              checkAgainstLookup: false,
              lookupMatchMessage: '',
              summaryGroup: '',
              helpText: '',
              placeholder: node.placeholder,
              rows: 4,
              maxLength: 1000,
            }
          : {
              id: inputId,
              visibleWhen: null,
              type: 'textInput',
              fieldName: DEFAULT_ANSWER_FIELD,
              label: '',
              required: node.required,
              unique: false,
              checkAgainstLookup: false,
              lookupMatchMessage: '',
              summaryGroup: '',
              helpText: '',
              placeholder: node.placeholder,
              inputMode: node.inputMode,
              maxLength: 200,
            },
      )
      break
    case 'dropdown':
      blocks.push({
        id: inputId,
        visibleWhen: null,
        type: 'select',
        fieldName: DEFAULT_ANSWER_FIELD,
        label: '',
        required: node.required,
        unique: false,
        checkAgainstLookup: false,
        lookupMatchMessage: '',
        summaryGroup: '',
        helpText: '',
        placeholder: '— เลือก —',
        options: node.options,
      })
      break
    case 'buttonChoice':
      blocks.push({
        id: inputId,
        visibleWhen: null,
        type: 'radioGroup',
        fieldName: DEFAULT_ANSWER_FIELD,
        label: '',
        required: node.required,
        unique: false,
        checkAgainstLookup: false,
        lookupMatchMessage: '',
        summaryGroup: '',
        helpText: '',
        options: node.options,
        layout: 'buttons',
      })
      break
    case 'multiSelect':
      blocks.push({
        id: inputId,
        visibleWhen: null,
        type: 'checkboxGroup',
        fieldName: DEFAULT_ANSWER_FIELD,
        label: '',
        required: node.required,
        unique: false,
        checkAgainstLookup: false,
        lookupMatchMessage: '',
        summaryGroup: '',
        helpText: '',
        options: node.options,
        maxSelect: node.maxSelect,
      })
      break
    case 'camera':
      blocks.push({
        id: inputId,
        visibleWhen: null,
        type: 'cameraCapture',
        fieldName: DEFAULT_PHOTO_FIELD,
        label: '',
        required: node.required,
        unique: false,
        checkAgainstLookup: false,
        lookupMatchMessage: '',
        summaryGroup: '',
        helpText: '',
        maxPhotos: node.maxPhotos,
      })
      break
    case 'end':
      if (node.message) {
        blocks.push({ id: `${node.id}__msg`, visibleWhen: null, type: 'paragraph', text: node.message })
      }
      break
    case 'form':
    case 'decision':
      break
  }
  return blocks
}

/** บล็อกที่จะเรนเดอร์จริงของโหนดหนึ่ง */
export function getRenderBlocks(node: FlowNode): Block[] {
  if (node.page && node.page.blocks.length > 0) return node.page.blocks
  return buildFallbackBlocks(node)
}

export function pageFromNode(node: FlowNode): PageDesign {
  return { schemaVersion: 1, blocks: buildFallbackBlocks(node) }
}

// ---------------------------------------------------------------------------
// การตรวจคำตอบ
// ---------------------------------------------------------------------------

export type FieldError = { fieldName: string; message: string }

function validateField(field: FieldInfo, value: AnswerValue | undefined): FieldError | null {
  const answered = isAnswered(value)

  if (!answered) {
    if (field.required) {
      return { fieldName: field.name, message: `กรุณาตอบ "${field.label}"` }
    }
    return null
  }

  switch (field.kind) {
    case 'number': {
      const num = typeof value === 'number' ? value : Number(String(value).trim())
      if (!Number.isFinite(num)) {
        return { fieldName: field.name, message: `"${field.label}" ต้องเป็นตัวเลข` }
      }
      return null
    }
    case 'text': {
      const text = typeof value === 'string' ? value : String(value)
      if (field.maxLength !== null && text.length > field.maxLength) {
        return {
          fieldName: field.name,
          message: `"${field.label}" ยาวเกิน ${field.maxLength} ตัวอักษร`,
        }
      }
      return null
    }
    case 'single': {
      if (field.options.length === 0) return null
      const text = typeof value === 'string' ? value : String(value)
      const allowed = field.options.map(optionValue)
      if (!allowed.includes(text)) {
        return { fieldName: field.name, message: `"${field.label}" มีค่าที่ไม่อยู่ในตัวเลือก` }
      }
      return null
    }
    case 'multi': {
      const selected = asStringArray(value)
      if (field.maxSelect !== null && selected.length > field.maxSelect) {
        return {
          fieldName: field.name,
          message: `"${field.label}" เลือกได้ไม่เกิน ${field.maxSelect} ข้อ`,
        }
      }
      if (field.options.length > 0) {
        const allowed = new Set(field.options.map(optionValue))
        if (selected.some((item) => !allowed.has(item))) {
          return { fieldName: field.name, message: `"${field.label}" มีค่าที่ไม่อยู่ในตัวเลือก` }
        }
      }
      return null
    }
    case 'photo': {
      const photos = asPhotoRefs(value)
      if (field.maxSelect !== null && photos.length > field.maxSelect) {
        return {
          fieldName: field.name,
          message: `"${field.label}" แนบรูปได้ไม่เกิน ${field.maxSelect} รูป`,
        }
      }
      return null
    }
    case 'gps':
      return null
  }
}

export type ValidationResult = { ok: true } | { ok: false; errors: FieldError[] }

export function validateStepAnswers(node: FlowNode, answers: StepAnswers): ValidationResult {
  // ★ ตรวจเฉพาะช่องที่ผู้ตอบมองเห็นจริงตอนนั้น
  // ช่องที่ถูกซ่อนอยู่แต่ตั้ง "บังคับตอบ" ไว้ จะทำให้กดถัดไปไม่ได้ตลอดกาลโดยไม่รู้สาเหตุ
  const visibleFieldNames = new Set(
    visibleBlocks(getRenderBlocks(node), answers)
      .filter(isInputBlock)
      .map((block) => block.fieldName),
  )

  const errors: FieldError[] = []
  for (const field of getNodeFields(node)) {
    if (field.origin === 'page' && !visibleFieldNames.has(field.name)) continue
    const error = validateField(field, answers[field.name])
    if (error) errors.push(error)
  }
  return errors.length === 0 ? { ok: true } : { ok: false, errors }
}
